import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/constants";

const PLEDGE_KEY = "nobsgs_pledge_accepted_v1";

export function shouldShowPledge() {
  try { return !localStorage.getItem(PLEDGE_KEY); }
  catch (err) { console.warn("[pledge] localStorage read failed:", err?.message || err); return false; }
}
export function markPledgeAccepted() {
  try { localStorage.setItem(PLEDGE_KEY, "1"); }
  catch (err) { console.warn("[pledge] localStorage write failed:", err?.message || err); }
}

export default function ZeroBSPledge({ open, onAccept, onClose }) {
  const [showStamp, setShowStamp] = useState(false);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setShowStamp(true), 500);
      // subtle vibration if available — swallow failures silently, this is purely cosmetic
      try { navigator.vibrate && navigator.vibrate([12, 40, 18]); }
      catch (err) { /* haptic feedback is optional; older browsers throw here */ void err; }
      return () => clearTimeout(t);
    } else {
      setShowStamp(false);
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.26 }}
          className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={onClose}
          data-testid="pledge-overlay"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
            className="w-full max-w-[560px] bg-[#1A2232] border border-[#26334D] rounded-t-3xl sm:rounded-3xl shadow-[0_-20px_60px_rgba(0,0,0,0.55)] p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="pledge-title"
          >
            <div className="flex items-center justify-between">
              <h2 id="pledge-title" className="text-lg font-semibold text-[#F8FAFC]">🔕 The Zero-BS Pledge</h2>
              <span className="text-xs text-[#94A3B8]">An open contract</span>
            </div>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Aspirant to aspirant.</p>

            <div className="mt-4 rounded-2xl border border-[#26334D] bg-[#111622] p-4">
              <p className="text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>
                Serious preparation happens in quiet libraries and long hours of focus. We refuse to treat you like a casual scroll-addict.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>
                <li className="flex gap-2"><span>🚫</span><span><strong>Zero Mid-Day Panic:</strong> No clickbait headlines, no fake urgency alerts, no course upsells.</span></li>
                <li className="flex gap-2"><span>🚫</span><span><strong>Zero Visual Clutter:</strong> No ad banners squeezed between your study cards.</span></li>
                <li className="flex gap-2"><span>🔕</span><span><strong>Silent Morning Digest Only:</strong> At most, one quiet ping at 7:30 AM. No sound, no buzz.</span></li>
              </ul>
            </div>

            <div className="mt-4 flex items-center justify-center">
              <AnimatePresence>
                {showStamp && (
                  <motion.div
                    key="stamp"
                    initial={{ scale: 1.35, rotate: -6, opacity: 0 }}
                    animate={{ scale: [1.35, 0.96, 1], rotate: [-6, 2, 0], opacity: [0, 1, 1] }}
                    transition={{ duration: 0.42, ease: "easeOut" }}
                    className="inline-flex items-center justify-center rounded-full border-2 border-[#F2BD68]/50 bg-[#F2BD68]/12 px-4 py-2 text-xs font-semibold tracking-[0.16em] text-[#F2BD68] shadow-[0_8px_24px_rgba(242,189,104,0.22)]"
                    aria-label="100 percent distraction-free guarantee"
                  >
                    ★ 100% DISTRACTION-FREE GUARANTEE ★
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid={TID.pledgeDismiss}
                className="w-full sm:w-1/3 rounded-xl border border-[#26334D] bg-[#1A2232] py-3 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors"
              >Read later</button>
              <button
                type="button"
                onClick={onAccept}
                data-testid={TID.pledgeAccept}
                className="w-full sm:flex-1 rounded-xl bg-[#E85A2A] text-[#F8FAFC] py-3 text-sm font-semibold shadow-[0_8px_24px_rgba(232,90,42,0.35)] hover:bg-[#C94A1D] transition-colors"
              >Enter Deep Work Mode →</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
